import React, { useEffect } from 'react';
import { Pricing } from '@spilca/order_ts/v1/order/model_pricing_pb';
import { useI18n } from '@monorepo/i18n';
import { ProductPackage } from '@spilca/dictionary_ts/v1/dictionary_md/product_package_pb';
import { Offer, Package } from '@spilca/order_ts/v1/order/model_offer_pb';
import styles from './index.module.css';

type PackageQuantityProps = {
  items: Pricing.AsObject[];
  productPackage: ProductPackage.AsObject[];
  packaging: Package.AsObject[];
  setPackaging: React.Dispatch<React.SetStateAction<Package.AsObject[]>>;
  offer: Offer.AsObject;
  readOnly: boolean;
};

export default function PackagingQuantity({
  items,
  productPackage,
  packaging,
  setPackaging,
  offer,
  readOnly
}: PackageQuantityProps) {
  const { t } = useI18n();

  const [proposedPriceMap, setProposedPriceMap] = React.useState<
    Record<number, number>
  >({});

  // Might be use full in future

  // useEffect(() => {
  //   if (!readOnly) return;
  //
  //   const nextMap: Record<number, number> = {};
  //
  //   packaging.forEach((it) => {
  //     nextMap[it.packageId] = offer.price/100 ?? 0;
  //   });
  //
  //   setProposedPriceMap(nextMap);
  // }, [readOnly, packaging, offer.price]);

  const getQuantity = (packageId: number): number =>
    packaging.find((pkg) => pkg.packageId === packageId)?.amount ?? 0;

  const updateQuantity = (packageId: number, newAmount: number) => {
    setPackaging((prev) => {
      const match = (p: Package.AsObject) => p.packageId === packageId;

      if (newAmount <= 0) {
        return prev.filter((p) => !match(p));
      }

      const idx = prev.findIndex(match);

      if (idx === -1) {
        return [
          ...prev,
          {
            packageId,
            amount: newAmount
          } as Package.AsObject
        ];
      }

      const next = [...prev];
      next[idx] = {
        ...next[idx],
        amount: newAmount
      };

      return next;
    });
  };

  const increment = (packageId: number) => {
    const current = getQuantity(packageId);
    updateQuantity(packageId, current + 1);
  };

  const decrement = (packageId: number) => {
    const current = getQuantity(packageId);
    updateQuantity(packageId, Math.max(0, current - 1));
  };

  const handleInputChange = (packageId: number, value: string) => {
    const parsed = parseInt(value, 10);
    const numValue = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    updateQuantity(packageId, numValue);
  };

  const totalQuantity = packaging.reduce(
    (sum, pkg) => sum + (pkg.amount || 0),
    0
  );

  // ---------- Column totals ----------
  let totalQuantityTotal = 0; // sum of quantityTotal column
  let totalOfferSum = 0; // sum of offersum column
  let totalOfferSumPrice = 0; // sum of offersumprice column
  let vat = 0;

  productPackage.forEach((pkg) => {
    const quantity = getQuantity(pkg.id);
    const quantityTotal = ((pkg.amount ?? 0) / 1000) * (quantity || 0);
    totalQuantityTotal += quantityTotal;

    const matchedItem = items.find((it) => it.packageId === pkg.id);
    const price =
      matchedItem?.pricingCondition?.pricePerItemMerchandiser ?? null;
    const vatRaw = matchedItem?.pricingCondition?.vatRate ?? null;
    vat = vatRaw ?? 0;

    const proposedPrice = proposedPriceMap[pkg.id];
    const hasProposedPrice =
      typeof proposedPrice === 'number' && !Number.isNaN(proposedPrice);

    if (hasProposedPrice) {
      totalOfferSum += proposedPrice * quantityTotal;
    }

    if (price != null) {
      totalOfferSumPrice += price * quantityTotal;
    }
  });

  const formatNumber = (value: number) =>
    Number.isNaN(value) ? '-' : value.toFixed(2);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.headerTitle}>
            {t('offer.form.packaging.title')}
          </h3>
        </div>
        <div className={styles.totalBadge}>
          {t('offer.form.packaging.total')}: <span>{totalQuantity}</span>
        </div>
      </div>

      <div className={styles.packageList}>
        <div className={styles.packageItem}>
          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.totalColumns')}
            </div>
            <div className={styles.packageName}>{}</div>
          </div>
          <div className={styles.wrapper}>
            <div className={styles.quantityControls}>{}</div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.quantity')}
            </div>
            <div className={styles.packageName}>
              {formatNumber(totalQuantityTotal)}
            </div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.price')}
            </div>
            <div className={styles.packageName}>-</div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.vatrate')}
            </div>
            <div className={styles.packageName}>-</div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.proposedPrice')}
            </div>
            <div className={styles.packageName}>
              {formatNumber(totalOfferSum / totalQuantityTotal)}
            </div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.proposedpricevat')}
            </div>
            <div className={styles.packageName}>
              {formatNumber(
                (totalOfferSum / totalQuantityTotal) * (1 + vat / 10000)
              )}
            </div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.offersum')}
            </div>
            <div className={styles.packageName}>
              {formatNumber(totalOfferSum)}
            </div>
          </div>

          <div className={styles.packageInfo}>
            <div className={styles.packageLabel}>
              {t('offer.form.packaging.offersumprice')}
            </div>
            <div className={styles.packageName}>
              {formatNumber(totalOfferSumPrice)}
            </div>
          </div>
        </div>

        {/* ROWS */}
        {productPackage.map((pkg) => {
          const quantity = getQuantity(pkg.id);
          const quantityTotal = ((pkg.amount ?? 0) / 1000) * (quantity || 0);

          const matchedItem = items.find((it) => it.packageId === pkg.id);
          const price = matchedItem?.pricingCondition?.pricePerItemMerchandiser;
          const currency = matchedItem?.pricingCondition?.currency;

          const priceLabel =
            price != null && currency ? `${price} ${currency}` : '-';

          const packageLabel =
            `${pkg.productName} | ${pkg.amount / 1000} / ${
              pkg.quantityTypeShortName
            }` ?? `#${pkg.id}`;

          const proposedPrice = proposedPriceMap[pkg.id] ?? '';

          const vatRaw = matchedItem?.pricingCondition?.vatRate;
          const vatFraction = vatRaw != null ? vatRaw / 10000 : null; // 2000 => 0.2 (20%)
          const proposedPriceVAT =
            proposedPrice !== '' && vatFraction != null
              ? (proposedPrice as number) * (1 + vatFraction)
              : '-';

          const offerSum =
            proposedPrice !== ''
              ? (proposedPrice as number) * quantityTotal
              : '-';

          const offerSumPrice = price != null ? price * quantityTotal : '-';

          return (
            <div key={pkg.id} className={styles.packageItem}>
              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {'PACKAGE ID: '}: {pkg.id}
                </div>
                <div className={styles.packageName}>{packageLabel}</div>
              </div>

              <div className={styles.wrapper}>
                <div className={styles.quantityControls}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary} ${
                      quantity === 0 ? styles.btnDisabled : ''
                    }`}
                    onClick={() => decrement(pkg.id)}
                    disabled={quantity === 0 || readOnly}
                  >
                    −
                  </button>

                  <input
                    className={styles.quantityInput}
                    type="number"
                    disabled={readOnly}
                    value={quantity}
                    onChange={(e) => handleInputChange(pkg.id, e.target.value)}
                    min="0"
                    inputMode="numeric"
                  />

                  <button
                    type="button"
                    disabled={readOnly}
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={() => increment(pkg.id)}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.quantity')}:
                </div>
                <div className={styles.packageName}>
                  {formatNumber(quantityTotal)}
                </div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.price')}:
                </div>
                <div className={styles.packageName}>{priceLabel}</div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.vatrate')}:
                </div>
                <div className={styles.packageName}>
                  {vatRaw != null ? `${vatRaw / 100}%` : '-'}
                </div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.proposedPrice')}
                </div>
                <input
                  type="number"
                  className={styles.quantityInput}
                  min="0"
                  value={proposedPrice}
                  disabled={readOnly}
                  onChange={(e) => {
                    const val = e.target.value;

                    setProposedPriceMap((prev) => {
                      if (val === '') {
                        const { [pkg.id]: _, ...rest } = prev;
                        return rest;
                      }

                      const num = Number(val);
                      if (Number.isNaN(num)) return prev;

                      return {
                        ...prev,
                        [pkg.id]: num
                      };
                    });
                  }}
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.proposedpricevat')}
                </div>
                <div className={styles.packageName}>
                  {typeof proposedPriceVAT === 'number'
                    ? formatNumber(proposedPriceVAT)
                    : proposedPriceVAT}
                </div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.offersum')}
                </div>
                <div className={styles.packageName}>
                  {typeof offerSum === 'number'
                    ? formatNumber(offerSum)
                    : offerSum}
                </div>
              </div>

              <div className={styles.packageInfo}>
                <div className={styles.packageLabel}>
                  {t('offer.form.packaging.offersumprice')}
                </div>
                <div className={styles.packageName}>
                  {typeof offerSumPrice === 'number'
                    ? formatNumber(offerSumPrice)
                    : offerSumPrice}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
